import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { ShiftTemplateCard } from './ShiftTemplateCard';
// ShiftTemplateModal removed
import {
  useShiftTemplates,
  useDeleteShiftTemplate
} from '@/hooks/useShiftTemplates';

import { captureEvent } from '@/lib/posthog';
import type { ShiftTemplate } from '@/types/shift-templates';
import { usePatientRole } from '@/hooks/usePatientRole';
import { toast } from 'sonner';
import { PATIENT_INACTIVE_TOAST_MESSAGE } from '@/lib/patient-status-errors';


interface ShiftTemplatesListProps {
  patientId: string;
}

export function ShiftTemplatesList({ patientId }: ShiftTemplatesListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isPatientActive } = usePatientRole(patientId);
  // Modal state removed
  // editingTemplate state removed
  const [deletingTemplate, setDeletingTemplate] = useState<ShiftTemplate | null>(null);

  const { data: templates, isLoading } = useShiftTemplates(patientId);
  const deleteMutation = useDeleteShiftTemplate();

  const handleCreate = () => {
    if (!isPatientActive) {
      toast.error(PATIENT_INACTIVE_TOAST_MESSAGE);
      return;
    }
    navigate(`/app/patients/${patientId}/templates/new`);
  };

  const handleEdit = (template: ShiftTemplate) => {
    if (!isPatientActive) {
      toast.error(PATIENT_INACTIVE_TOAST_MESSAGE);
      return;
    }
    navigate(`/app/patients/${patientId}/templates/${template.id}`);
  };

  const handleDelete = (template: ShiftTemplate) => {
    if (!isPatientActive) {
      toast.error(PATIENT_INACTIVE_TOAST_MESSAGE);
      return;
    }
    setDeletingTemplate(template);
  };

  const confirmDelete = async () => {
    if (deletingTemplate) {
      await deleteMutation.mutateAsync({
        id: deletingTemplate.id,
        patientId,
      });

      // PostHog Tracking
      captureEvent('shift_template_deleted', {
        patient_id: patientId,
        template_id: deletingTemplate.id,
        name: deletingTemplate.name
      });

      setDeletingTemplate(null);
    }
  };



  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            {t('shiftTemplates.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {t('shiftTemplates.title')}
              </CardTitle>
              <p className="text-xs text-muted-foreground max-w-md">
                {t('shiftTemplates.listDescription')}
              </p>
            </div>
            <Button size="sm" onClick={handleCreate} disabled={!isPatientActive}>
              <Plus className="h-4 w-4 mr-1" />
              {t('shiftTemplates.new')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {templates && templates.length > 0 ? (
            <div className="grid gap-3">
              {templates.map((template) => (
                <ShiftTemplateCard
                  key={template.id}
                  template={template}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              bare
              icon={Clock}
              title={t('shiftTemplates.emptyState')}
              description={t('shiftTemplates.emptyStateDescription')}
            />
          )}
        </CardContent>
      </Card>



      <AlertDialog
        open={!!deletingTemplate}
        onOpenChange={(open) => !open && setDeletingTemplate(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('shiftTemplates.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('shiftTemplates.deleteConfirmDescription', { name: deletingTemplate?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
